import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { Cookies } from "react-cookie-consent";
import { ToastContainer } from "react-toastify";
import config from "../../config";
import CookieConsentBanner from "../../components/CookieConsentBanner";
import { removeAnalyticCookies } from "../../components/CookieConsentBanner";
import React from "react";
import { PlatformProvider } from "../../contexts/PlatformContext";
import { UsageProvider } from "../../contexts/UsageContext";

const Auth = React.lazy(() => import("../Auth"));
const Dashboard = React.lazy(() => import("../Dashboard"));
const Legal = React.lazy(() => import("../Legal"));
const PaymentStatus = React.lazy(() => import("../PaymentStatus"));
const PageNotFound = React.lazy(() => import("../PageNotFound"));
const PlatformManagement = React.lazy(() => import("../PlatformManagement"));
const UsageManagement = React.lazy(() => import("../UsageManagement"));
const Settings = React.lazy(() => import("../Settings"));

// Route paths
export const homeRoutePath = "/";
export const dashboardRoutePath = "/dashboard";
export const legalRoutePath = "/legal";
export const onboardingRoutePath = "/onboarding";
export const paymentStatusRoutePath = "/paymentstatus";
export const platformManagementRoutePath = "/platforms";
export const usageManagementRoutePath = "/usage";
export const settingsRoutePath = "/settings";

interface ProtectedRouteProps {
  redirectPath?: string;
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = () => {
  // Check if the user has given consent for analytics cookies
  const consentCookie = Cookies.get(config.cookies.consentCookieLabel);
  if (consentCookie && consentCookie === "false") {
    // If not, remove the analytics cookies
    removeAnalyticCookies();
  }

  // NOTE: Any other additional protected route logic can be added here...

  // Render the child routes
  return <Outlet />;
};

const CoreRouter: React.FC = () => {
  return (
    <Router>
      <div className="main">
        <Routes>
          {/* Public Routes */}
          <Route path={homeRoutePath} element={<Auth />} />
          <Route path={`${legalRoutePath}/:policyType`} element={<Legal />} />
          <Route
            path={`${paymentStatusRoutePath}/*`}
            element={<PaymentStatus />}
          />
          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route
              path={`${dashboardRoutePath}/*`}
              element={
                <PlatformProvider>
                  <Dashboard />
                </PlatformProvider>
              }
            />
            <Route
              path={`${platformManagementRoutePath}/*`}
              element={
                <PlatformProvider>
                  <PlatformManagement />
                </PlatformProvider>
              }
            />
            <Route
              path={`${usageManagementRoutePath}/*`}
              element={
                <PlatformProvider>
                  <UsageProvider>
                    <UsageManagement />
                  </UsageProvider>
                </PlatformProvider>
              }
            />
            <Route
              path={`${settingsRoutePath}/*`}
              element={
                <PlatformProvider>
                  <Settings />
                </PlatformProvider>
              }
            />
          </Route>
          {/* Catch-all / Fallback */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
        <CookieConsentBanner />
        <ToastContainer />
      </div>
    </Router>
  );
};

export default CoreRouter;
